/**
 * 벌점 관리 훅
 */
import { useState, useCallback, useEffect } from 'react';
import { useAuthStore } from '../../auth/stores/AuthStore';
import { penaltyService } from '../services/PenaltyService';
import {
  PenaltyType,
  PenaltyTypeCreate,
  PenaltyTypeUpdate,
  PenaltySetting,
  PenaltySettingUpdate,
  PenaltyThreshold,
  PenaltyThresholdCreate,
  PenaltyThresholdUpdate,
  PenaltyRecord,
  PenaltyRecordCreate,
  PenaltyRecordFilter,
  PenaltySummary,
  PenaltySummaryFilter,
  PenaltyStatistics,
} from '../models/Penalty';

export interface UsePenaltyManagementReturn {
  // 상태
  types: PenaltyType[];
  thresholds: PenaltyThreshold[];
  setting: PenaltySetting | null;
  records: PenaltyRecord[];
  summaries: PenaltySummary[];
  statistics: PenaltyStatistics | null;
  isLoading: boolean;
  error: string | null;

  // 페이지네이션 상태
  recordsTotal: number;
  recordsPage: number;
  summariesTotal: number;
  summariesPage: number;

  // 벌점 유형 관리
  loadTypes: (includeInactive?: boolean) => Promise<void>;
  createType: (data: Omit<PenaltyTypeCreate, 'organization_id'>) => Promise<void>;
  updateType: (typeId: string, data: PenaltyTypeUpdate) => Promise<void>;
  deleteType: (typeId: string) => Promise<void>;
  initTypes: () => Promise<{ created_count: number; message: string }>;

  // 임계값 관리
  loadThresholds: (includeInactive?: boolean) => Promise<void>;
  createThreshold: (data: Omit<PenaltyThresholdCreate, 'organization_id'>) => Promise<void>;
  updateThreshold: (thresholdId: string, data: PenaltyThresholdUpdate) => Promise<void>;
  deleteThreshold: (thresholdId: string) => Promise<void>;

  // 설정 관리
  loadSetting: () => Promise<void>;
  updateSetting: (data: PenaltySettingUpdate) => Promise<void>;

  // 벌점 기록 관리
  loadRecords: (filter?: PenaltyRecordFilter, page?: number) => Promise<void>;
  createRecord: (data: Omit<PenaltyRecordCreate, 'organization_id'>) => Promise<void>;
  cancelRecord: (recordId: string, reason: string) => Promise<void>;
  reduceRecord: (recordId: string, reducedPoints: number, reason: string) => Promise<void>;
  cancelReduction: (recordId: string) => Promise<void>;
  restoreRecord: (recordId: string) => Promise<void>;

  // 현황 조회
  loadSummaries: (filter?: PenaltySummaryFilter, page?: number) => Promise<void>;
  loadStudentSummary: (studentId: string) => Promise<PenaltySummary | null>;

  // 통계/초기화
  loadStatistics: () => Promise<void>;
  resetPenalties: () => Promise<number>;

  // 유틸리티
  clearError: () => void;
  refresh: () => Promise<void>;
}

// localStorage에서 organization_id 가져오기 (폴백)
const getOrganizationIdFromStorage = (): string => {
  try {
    // 1. 직접 저장된 organization_id 키
    const orgId = localStorage.getItem('organization_id');
    if (orgId) return orgId;

    // 2. organizationId 키 (camelCase)
    const orgIdCamel = localStorage.getItem('organizationId');
    if (orgIdCamel) return orgIdCamel;

    // 3. user 객체에서 가져오기
    const user = localStorage.getItem('user');
    if (user) {
      const parsed = JSON.parse(user);
      if (parsed.organization_id) return parsed.organization_id;
    }

    // 4. cached_organization_info에서 가져오기
    const cachedOrg = localStorage.getItem('cached_organization_info');
    if (cachedOrg) {
      const parsed = JSON.parse(cachedOrg);
      if (parsed.id) return parsed.id;
    }
  } catch (e) {
    console.error('[PenaltyManagement] localStorage 파싱 오류:', e);
  }
  return '';
};

export function usePenaltyManagement(): UsePenaltyManagementReturn {
  const { user } = useAuthStore();

  // user에서 가져오고, 없으면 localStorage 폴백
  const organizationId = user?.organization_id || getOrganizationIdFromStorage();

  // 디버그 로그

  // 상태
  const [types, setTypes] = useState<PenaltyType[]>([]);
  const [thresholds, setThresholds] = useState<PenaltyThreshold[]>([]);
  const [setting, setSetting] = useState<PenaltySetting | null>(null);
  const [records, setRecords] = useState<PenaltyRecord[]>([]);
  const [summaries, setSummaries] = useState<PenaltySummary[]>([]);
  const [statistics, setStatistics] = useState<PenaltyStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 페이지네이션 상태
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsPage, setRecordsPage] = useState(1);
  const [summariesTotal, setSummariesTotal] = useState(0);
  const [summariesPage, setSummariesPage] = useState(1);

  // 에러 처리 헬퍼
  const handleError = useCallback((e: unknown, defaultMessage: string) => {
    const message = e instanceof Error ? e.message : defaultMessage;
    setError(message);
    console.error(message, e);
  }, []);

  // 벌점 유형 관리
  const loadTypes = useCallback(
    async (includeInactive = false) => {
      if (!organizationId) {
        return;
      }
      setIsLoading(true);
      try {
        const data = await penaltyService.types.getTypes(organizationId, includeInactive);
        setTypes(data);
        setError(null);
      } catch (e) {
        console.error('[PenaltyManagement] getTypes error:', e);
        handleError(e, '벌점 유형 조회 실패');
      } finally {
        setIsLoading(false);
      }
    },
    [organizationId, handleError]
  );

  const createType = useCallback(
    async (data: Omit<PenaltyTypeCreate, 'organization_id'>) => {
      if (!organizationId) return;
      setIsLoading(true);
      try {
        await penaltyService.types.createType({
          ...data,
          organization_id: organizationId,
        });
        await loadTypes();
        setError(null);
      } catch (e) {
        handleError(e, '벌점 유형 생성 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [organizationId, loadTypes, handleError]
  );

  const updateType = useCallback(
    async (typeId: string, data: PenaltyTypeUpdate) => {
      setIsLoading(true);
      try {
        await penaltyService.types.updateType(typeId, data);
        await loadTypes();
        setError(null);
      } catch (e) {
        handleError(e, '벌점 유형 수정 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [loadTypes, handleError]
  );

  const deleteType = useCallback(
    async (typeId: string) => {
      setIsLoading(true);
      try {
        await penaltyService.types.deleteType(typeId);
        await loadTypes();
        setError(null);
      } catch (e) {
        handleError(e, '벌점 유형 삭제 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [loadTypes, handleError]
  );

  const initTypes = useCallback(async (): Promise<{ created_count: number; message: string }> => {
    if (!organizationId) return { created_count: 0, message: 'organization_id가 없습니다.' };
    setIsLoading(true);
    try {
      const result = await penaltyService.types.initTypes(organizationId);
      await loadTypes(true);  // include_inactive=true로 새로 생성된 유형도 포함
      setError(null);
      return result;
    } catch (e) {
      handleError(e, '기본 유형 초기화 실패');
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, loadTypes, handleError]);

  // 임계값 관리
  const loadThresholds = useCallback(
    async (includeInactive = false) => {
      if (!organizationId) return;
      setIsLoading(true);
      try {
        const data = await penaltyService.thresholds.getThresholds(
          organizationId,
          includeInactive
        );
        setThresholds(data);
        setError(null);
      } catch (e) {
        handleError(e, '임계값 조회 실패');
      } finally {
        setIsLoading(false);
      }
    },
    [organizationId, handleError]
  );

  const createThreshold = useCallback(
    async (data: Omit<PenaltyThresholdCreate, 'organization_id'>) => {
      if (!organizationId) return;
      setIsLoading(true);
      try {
        await penaltyService.thresholds.createThreshold({
          ...data,
          organization_id: organizationId,
        });
        await loadThresholds();
        setError(null);
      } catch (e) {
        handleError(e, '임계값 생성 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [organizationId, loadThresholds, handleError]
  );

  const updateThreshold = useCallback(
    async (thresholdId: string, data: PenaltyThresholdUpdate) => {
      setIsLoading(true);
      try {
        await penaltyService.thresholds.updateThreshold(thresholdId, data);
        await loadThresholds();
        setError(null);
      } catch (e) {
        handleError(e, '임계값 수정 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [loadThresholds, handleError]
  );

  const deleteThreshold = useCallback(
    async (thresholdId: string) => {
      setIsLoading(true);
      try {
        await penaltyService.thresholds.deleteThreshold(thresholdId);
        await loadThresholds();
        setError(null);
      } catch (e) {
        handleError(e, '임계값 삭제 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [loadThresholds, handleError]
  );

  // 설정 관리
  const loadSetting = useCallback(async () => {
    if (!organizationId) return;
    setIsLoading(true);
    try {
      const data = await penaltyService.settings.getSetting(organizationId);
      setSetting(data);
      setError(null);
    } catch (e) {
      handleError(e, '설정 조회 실패');
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, handleError]);

  const updateSetting = useCallback(
    async (data: PenaltySettingUpdate) => {
      if (!organizationId) return;
      setIsLoading(true);
      try {
        await penaltyService.settings.updateSetting(organizationId, data);
        await loadSetting();
        setError(null);
      } catch (e) {
        handleError(e, '설정 수정 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [organizationId, loadSetting, handleError]
  );

  // 벌점 기록 관리
  const loadRecords = useCallback(
    async (filter?: PenaltyRecordFilter, page: number = 1) => {
      if (!organizationId) return;
      setIsLoading(true);
      try {
        // ⭐ 전체 로드(큰 page_size) → 기록 탭에서 학생 이름 클라이언트 검색/페이지네이션
        const result: any = await (penaltyService.records.getRecords as any)(organizationId, filter, 1, 500);
        const items = Array.isArray(result) ? result : (result?.items ?? []);
        const total = Array.isArray(result) ? result.length : (result?.total ?? items.length);
        const currentPage = Array.isArray(result) ? page : (result?.page ?? page);
        setRecords(items);
        setRecordsTotal(total);
        setRecordsPage(currentPage);
        setError(null);
      } catch (e) {
        handleError(e, '벌점 기록 조회 실패');
      } finally {
        setIsLoading(false);
      }
    },
    [organizationId, handleError]
  );

  const createRecord = useCallback(
    async (data: Omit<PenaltyRecordCreate, 'organization_id'>) => {
      if (!organizationId) return;
      setIsLoading(true);
      try {
        await penaltyService.records.createRecord({
          ...data,
          organization_id: organizationId,
        });
        await loadRecords();
        setError(null);
      } catch (e) {
        handleError(e, '벌점 부과 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [organizationId, loadRecords, handleError]
  );

  const cancelRecord = useCallback(
    async (recordId: string, reason: string) => {
      setIsLoading(true);
      try {
        await penaltyService.records.cancelRecord(recordId, reason);
        await loadRecords();
        setError(null);
      } catch (e) {
        handleError(e, '벌점 취소 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [loadRecords, handleError]
  );

  const reduceRecord = useCallback(
    async (recordId: string, reducedPoints: number, reason: string) => {
      setIsLoading(true);
      try {
        await (penaltyService.records as any).reduceRecord(recordId, reducedPoints, reason);
        await loadRecords();
        setError(null);
      } catch (e) {
        handleError(e, '벌점 감경 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [loadRecords, handleError]
  );

  const restoreRecord = useCallback(
    async (recordId: string) => {
      setIsLoading(true);
      try {
        await (penaltyService.records as any).restoreRecord(recordId);
        await loadRecords();
        setError(null);
      } catch (e) {
        handleError(e, '벌점 복구 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [loadRecords, handleError]
  );

  const cancelReduction = useCallback(
    async (recordId: string) => {
      setIsLoading(true);
      try {
        await (penaltyService.records as any).cancelReduction(recordId);
        await loadRecords();
        setError(null);
      } catch (e) {
        handleError(e, '감경 취소 실패');
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [loadRecords, handleError]
  );

  // 현황 조회
  const loadSummaries = useCallback(
    async (filter?: PenaltySummaryFilter, page: number = 1) => {
      if (!organizationId) return;
      setIsLoading(true);
      try {
        // ⭐ 전체 로드(큰 page_size) → 현황 탭에서 학생 이름 클라이언트 검색/페이지네이션
        const result: any = await (penaltyService.summaries.getSummaries as any)(organizationId, filter, 1, 500);
        const items = Array.isArray(result) ? result : (result?.items ?? []);
        const total = Array.isArray(result) ? result.length : (result?.total ?? items.length);
        const currentPage = Array.isArray(result) ? page : (result?.page ?? page);
        setSummaries(items);
        setSummariesTotal(total);
        setSummariesPage(currentPage);
        setError(null);
      } catch (e) {
        handleError(e, '현황 조회 실패');
      } finally {
        setIsLoading(false);
      }
    },
    [organizationId, handleError]
  );

  const loadStudentSummary = useCallback(
    async (studentId: string): Promise<PenaltySummary | null> => {
      if (!organizationId) return null;
      try {
        const data = await penaltyService.summaries.getStudentSummary(
          studentId,
          organizationId
        );
        return data;
      } catch (e) {
        handleError(e, '학생 현황 조회 실패');
        return null;
      }
    },
    [organizationId, handleError]
  );

  // 통계/초기화
  const loadStatistics = useCallback(async () => {
    if (!organizationId) return;
    setIsLoading(true);
    try {
      const data = await penaltyService.statistics.getStatistics(organizationId);
      setStatistics(data);
      setError(null);
    } catch (e) {
      handleError(e, '통계 조회 실패');
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, handleError]);

  const resetPenalties = useCallback(async (): Promise<number> => {
    if (!organizationId) return 0;
    setIsLoading(true);
    try {
      const result = await penaltyService.statistics.resetPenalties(organizationId);
      await loadSummaries();
      await loadStatistics();
      setError(null);
      return result.reset_count;
    } catch (e) {
      handleError(e, '벌점 초기화 실패');
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, loadSummaries, loadStatistics, handleError]);

  // 유틸리티
  const clearError = useCallback(() => setError(null), []);

  const refresh = useCallback(async () => {
    await Promise.all([
      loadTypes(),
      loadThresholds(),
      loadSetting(),
      loadRecords(),
      loadSummaries(),
      loadStatistics(),
    ]);
  }, [loadTypes, loadThresholds, loadSetting, loadRecords, loadSummaries, loadStatistics]);

  // 초기 로드
  useEffect(() => {
    if (organizationId) {
      loadTypes();
      loadThresholds();
      loadSetting();
    }
  }, [organizationId, loadTypes, loadThresholds, loadSetting]);

  return {
    // 상태
    types,
    thresholds,
    setting,
    records,
    summaries,
    statistics,
    isLoading,
    error,

    // 페이지네이션 상태
    recordsTotal,
    recordsPage,
    summariesTotal,
    summariesPage,

    // 벌점 유형 관리
    loadTypes,
    createType,
    updateType,
    deleteType,
    initTypes,

    // 임계값 관리
    loadThresholds,
    createThreshold,
    updateThreshold,
    deleteThreshold,

    // 설정 관리
    loadSetting,
    updateSetting,

    // 벌점 기록 관리
    loadRecords,
    createRecord,
    cancelRecord,
    reduceRecord,
    cancelReduction,
    restoreRecord,

    // 현황 조회
    loadSummaries,
    loadStudentSummary,

    // 통계/초기화
    loadStatistics,
    resetPenalties,

    // 유틸리티
    clearError,
    refresh,
  };
}
